// frontend/src/admin/v2/components/IntegrationCard.jsx
import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Stack, Typography, Box, Chip, Collapse, IconButton,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import IntegrationMonogram from './IntegrationMonogram'
import { SP, FS, hoverOnly } from '../../../theme/tokens'
import Panel from '../../../components/common/Panel'
import RowActionsMenu from '../../../components/common/RowActionsMenu'
import { formatBerlinDate } from '../../../utils/time'
import { etikett, ton } from '../adminStatus'

/**
 * IntegrationCard — one card per external service on /admin/integrations.
 *
 * @description
 *   Shows monogram + name + kind subtitle, status pill (green/amber/red),
 *   last successful probe time, auth type, secret-env-var summary, and a
 *   "Test connection" button that calls the live probe endpoint
 *   synchronously.
 *
 *   Click expand → reveals notes, last probe timing, secret env vars
 *   with last-4 of the value (never the full value).
 *
 * @useWhen
 *   - Inside IntegrationsList grid
 *
 * @props
 *   integration: serialized payload from /api/admin/integrations (see
 *                backend/admin_v2/integrations.py:_serialize)
 *   onProbe:     async (key) => updatedIntegration  — caller refreshes
 *                state with the returned payload
 *   delay:       number — stagger animation delay in ms
 */
// 🔴 G14a-1: die Abbildung Status → Etikett/Farbe steht seit dem 2026-10-31 an
// EINER Stelle (`adminStatus.js`). Die Karte sagte vorher „Operational / Needs
// attention / Issue" fuer dieselben drei Serverwerte, die die Liste daneben
// „green / needs attention / issues" nannte.


function relTime(iso) {
  if (!iso) return 'never probed'
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return formatBerlinDate(d)
}


export default function IntegrationCard({ integration, onProbe, delay = 0 }) {
  const [expanded, setExpanded] = useState(false)
  const [probing, setProbing] = useState(false)

  const farbe = ton(integration.status)
  const probeKind = integration.probe_kind || 'configured_only'
  const recent = integration.recent_probe
  const stats = integration.stats_24h || {}

  const handleProbe = async () => {
    if (probing) return
    setProbing(true)
    try {
      await onProbe?.(integration.key)
    } finally {
      setProbing(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: delay / 1000, ease: 'easeOut' }}
    >
      <Panel pad="card" data-testid={`karte-kennung-${integration.key}`}>
        {/* Top row — monogram + name/kind + status pill */}
        <Stack direction="row" alignItems="flex-start" spacing={SP.card}>
          <IntegrationMonogram
            letter={integration.monogram_letter || (integration.name || '?')[0]}
            tint={integration.monogram_tint || integration.key}
          />
          <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
            <Typography
              variant="subtitle1"
              data-testid={`karte-titel-${integration.key}`}
              sx={{ fontSize: FS.md, fontWeight: 600, lineHeight: 1.3 }}
            >
              {integration.name}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
              {integration.kind}
            </Typography>
          </Stack>
          <Chip
            data-testid={`karte-status-${integration.key}`}
            label={etikett(integration.status)}
            size="small"
            sx={{
              fontSize: FS.xs,
              fontWeight: 600,
              color: farbe,
              bgcolor: 'transparent',
              border: 1,
              borderColor: farbe,
            }}
          />
        </Stack>

        {/* Mid row — last probe + stats + auth type */}
        <Stack spacing={0.25} sx={{ mt: SP.card }}>
          <Stack direction="row" justifyContent="space-between" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            <span>Last probe</span>
            <span style={{ fontWeight: 500 }}>{relTime(recent?.checked_at)}</span>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            <span>Auth</span>
            <span style={{ fontWeight: 500 }}>{integration.auth_type}</span>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            <span>24h probes</span>
            <span style={{ fontWeight: 500 }}>
              {stats.total || 0} ({stats.errors || 0} failed)
            </span>
          </Stack>
        </Stack>

        {/* Bottom action row.
            🔴 G14a-1 (Plan §2.3): „Test connection" wandert ins ⋮. Der Grund
            ist P19 — eine Kartenzeile traegt HOECHSTENS EINEN sichtbaren Knopf
            neben dem ⋮, und der Aufklapper ist kein solcher (er traegt
            `aria-expanded`). Der Aufruf bleibt unveraendert (`onProbe`); nur
            sein Ort aendert sich. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={SP.tight}
          sx={{ mt: SP.card }}
        >
          {probing && (
            <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
              Testing…
            </Typography>
          )}
          <RowActionsMenu
            label={`Row actions for ${integration.name}`}
            actions={[{
              key: 'probe',
              // Der Wortlaut nennt, was der Aufruf TUT: eine echte Netzpruefung
              // oder nur die Pruefung der hinterlegten Werte. Vorher stand das
              // im Tooltip des Knopfes — ein Tooltip ist per Tastatur und auf
              // dem Telefon nicht erreichbar (Regel 22 Nr. 8).
              label: probing
                ? 'Testing…'
                : (probeKind === 'live' ? 'Test connection' : 'Verify secrets'),
              icon: <RefreshIcon sx={{ fontSize: 16 }} />,
              disabled: probing,
              onClick: handleProbe,
            }]}
          />
          <IconButton
            size="small"
            onClick={() => setExpanded(e => !e)}
            aria-label={expanded ? 'Hide details' : 'Show details'}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        {/* Expanded details — secret vars (names + last-4), notes, runbook */}
        <Collapse in={expanded} unmountOnExit timeout={180}>
          <Box sx={{ mt: SP.card, pt: SP.card, borderTop: 1, borderColor: 'divider' }}>
            <Stack spacing={SP.tight}>
              <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                Secrets
              </Typography>
              <Stack spacing={0.25}>
                {(integration.secrets?.vars || []).map(v => (
                  <Stack key={v.name} direction="row" justifyContent="space-between" sx={{ fontSize: FS.xs, fontFamily: 'monospace' }}>
                    <Box component="span" sx={{ color: v.present ? 'text.primary' : 'error.main' }}>
                      {v.name}
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {v.present
                        ? (v.via ? `…${v.last4} (via ${v.via})` : `…${v.last4}`)
                        : 'missing'}
                    </Box>
                  </Stack>
                ))}
                {(integration.secrets?.vars || []).length === 0 && (
                  <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled', fontStyle: 'italic' }}>
                    No secret env vars listed in registry
                  </Typography>
                )}
              </Stack>

              {recent?.error && (
                <>
                  <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'error.main', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, mt: 1 }}>
                    Last probe error
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: FS.xs, fontFamily: 'monospace', color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                    {recent.error}
                  </Typography>
                </>
              )}

              {integration.notes && (
                <>
                  <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, mt: 1 }}>
                    Notes
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                    {integration.notes.trim()}
                  </Typography>
                </>
              )}

              {integration.runbook && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  <OpenInNewIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  <Typography
                    component="a"
                    href={`https://github.com/BrewoutCoffeeBot/purchase-order-generator/blob/master/docs/knowledge/${integration.runbook}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    sx={{ fontSize: FS.xs, color: 'primary.main', textDecoration: 'none', ...hoverOnly({ textDecoration: 'underline' }) }}
                  >
                    Runbook · docs/knowledge/{integration.runbook}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Collapse>
      </Panel>
    </motion.div>
  )
}
